import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import type { LoginResponse, UserDTO } from '@bruno/shared';
import { isTwoFactorChallenge } from '@bruno/shared';
import * as authApi from '../api/auth';
import { setAccessToken } from '../api/client';

interface AuthCtx {
  user: UserDTO | null;
  loading: boolean;
  /** Returns the raw LoginResponse so the caller can branch on a 2FA
   *  challenge. When the result is the regular success shape AND the
   *  user is actually an admin, `user` state is updated. Non-admins
   *  are logged out and an error is thrown. */
  login: (email: string, password: string, captchaToken?: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  setUser: (u: UserDTO | null) => void;
  /** Re-fetches the current admin from /v1/auth/me so derived UI (e.g.
   *  the 2FA nudge banner) reflects self-service changes immediately. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void authApi.tryRestoreSession().then((u) => {
      if (cancelled) return;
      // Only allow admins on this app.
      if (u && u.role !== 'admin') {
        void authApi.logout();
        setUser(null);
      } else {
        setUser(u);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
      const res = await authApi.login({ email, password, captchaToken });
      if (isTwoFactorChallenge(res)) {
        // Defer the admin-role check until after 2FA exchange. The
        // LoginPage will call setUser via the success callback of the
        // /2fa/login round-trip.
        return res;
      }
      if (res.user.role !== 'admin') {
        await authApi.logout();
        throw new Error('This account is not an administrator.');
      }
      setUser(res.user);
      return res;
    },
    [],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setAccessToken(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { user: fresh } = await authApi.getMe();
      // Stay consistent with the admin-only gate enforced on login.
      if (fresh.role !== 'admin') {
        await authApi.logout();
        setUser(null);
        return;
      }
      setUser(fresh);
    } catch {
      // Non-fatal; the next refresh/navigation reconciles.
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
